import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

import media from '../../../utils/mediaQueries';
import Btn from '../../buttons/Btn';
import { matchCountries } from '../funnel/fields';
import {
  colors, radius, shadows, spacing,
} from '../tokens';
import { CountryExchange, useCountryExchangeData } from './useCountryExchangeData';

const ANTS_EXCHANGE_URL = 'https://permisdeconduire.ants.gouv.fr/demarches-en-ligne';
const NEPH_FLOW_URL = '/demarches-permis/numero-neph';

const Wrapper = styled.div`
  max-width: 820px;
  margin: 0 auto;
  padding: ${spacing.xl};
  background-color: ${colors.surface};
  border: 1px solid ${colors.border};
  border-radius: ${radius.large};
  box-shadow: ${shadows.card};
  position: relative;

  ${media.mobile`
    padding: 20px;
  `}
`;

const InputWrapper = styled.div`
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  height: 48px;
  padding: 0 ${spacing.md};
  border: 1px solid ${colors.border};
  border-radius: 2px;
  background-color: #f4f7f9;
  font-size: 14px;
  color: ${colors.text};
  outline: none;

  &:focus {
    border-color: ${colors.brand};
  }
`;

const Dropdown = styled.ul`
  position: absolute;
  z-index: 5;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 240px;
  overflow-y: auto;
  margin: 0;
  padding: ${spacing.xs} 0;
  list-style: none;
  background-color: ${colors.surface};
  border: 1px solid ${colors.border};
  border-radius: ${radius.medium};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
`;

const NoResultItem = styled.li`
  padding: ${spacing.sm} ${spacing.md};
  font-size: 14px;
  color: ${colors.textMuted};
`;

const DropdownItem = styled.li`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  padding: ${spacing.sm} ${spacing.md};
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background-color: ${colors.brandSoft};
  }
`;

const RegionSelect = styled.select`
  width: 100%;
  height: 48px;
  margin-top: ${spacing.md};
  padding: 0 ${spacing.md};
  border: 1px solid ${colors.border};
  border-radius: 2px;
  background-color: #f4f7f9;
  font-size: 14px;
  color: ${colors.text};
`;

const ResultPanel = styled.div`
  margin-top: ${spacing.lg};
  padding: ${spacing.lg};
  border: 1px solid ${colors.border};
  border-radius: ${radius.medium};
`;

const ResultHeading = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  font-size: 18px;
  font-weight: 800;
  color: ${colors.text};
  margin-bottom: ${spacing.md};
`;

type BadgeTone = 'success' | 'warning' | 'danger' | 'neutral';

const BADGE_COLORS: Record<BadgeTone, { background: string; text: string }> = {
  success: { background: '#e3f6e8', text: '#1c7c3f' },
  warning: { background: '#fff4de', text: '#a56a00' },
  danger: { background: '#fde8e8', text: '#c22a2a' },
  neutral: { background: colors.brandSoft, text: colors.brand },
};

const Badge = styled.span<{ tone: BadgeTone }>`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  background-color: ${({ tone }) => BADGE_COLORS[tone].background};
  color: ${({ tone }) => BADGE_COLORS[tone].text};
`;

const ResultRow = styled.p`
  font-size: 14px;
  line-height: 1.5;
  color: ${colors.text};
  margin: ${spacing.sm} 0;
`;

const ResultRowLabel = styled.span`
  font-weight: 700;
`;

const ResultLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  padding: 0 ${spacing.lg};
  border: 1px solid ${colors.brand};
  border-radius: 4px;
  background-color: ${colors.surface};
  color: ${colors.brand};
  font-size: 16px;
  font-weight: bold;
  text-decoration: none;
  transition: opacity 0.3s;

  &:hover {
    opacity: 0.9;
    text-decoration: none;
  }
`;

const STATUS_TONE: Record<string, BadgeTone> = {
  oui: 'success',
  eu_eee: 'success',
  reconnaissance: 'success',
  conditionnel: 'warning',
  non: 'danger',
};

const STATUS_BADGE_LABEL: Record<string, string> = {
  oui: 'Échangeable',
  eu_eee: 'Reconnu UE / EEE',
  reconnaissance: 'Reconnu',
  conditionnel: 'Sous conditions',
  non: 'Non échangeable',
};

const toneForStatus = (status: string): BadgeTone => STATUS_TONE[status] || 'neutral';
const labelForStatus = (status: string): string => STATUS_BADGE_LABEL[status] || 'À vérifier';

type ExchangeResult = {
  name: string;
  flag: string;
  status: string;
  statusText: string;
  specialStatus: string;
  specialText: string;
  showSpecialRow: boolean;
};

const buildResult = (country: CountryExchange | null, regionName: string | null): ExchangeResult | null => {
  if (!country) {
    return null;
  }

  if (country.multiregion) {
    const region = country.regions?.find((candidate) => candidate.name === regionName);
    if (!region) {
      return null;
    }
    return {
      name: `${country.name} — ${region.name}`,
      flag: country.flag,
      status: region.status,
      statusText: region.statusText,
      specialStatus: region.specialStatus,
      specialText: region.specialText,
      showSpecialRow: true,
    };
  }

  return {
    name: country.name,
    flag: country.flag,
    status: country.status,
    statusText: country.statusText,
    specialStatus: country.specialStatus,
    specialText: country.specialText,
    showSpecialRow: country.category !== 'eu_eee',
  };
};

const CountryExchangeTool: React.FC = () => {
  const { countries, isLoading } = useCountryExchangeData();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<CountryExchange | null>(null);
  const [selectedRegionName, setSelectedRegionName] = useState<string | null>(null);

  const matches = useMemo(() => matchCountries(query, countries), [query, countries]);
  const result = useMemo(
    () => buildResult(selectedCountry, selectedRegionName),
    [selectedCountry, selectedRegionName],
  );

  const selectCountry = (country: CountryExchange): void => {
    setSelectedCountry(country);
    setSelectedRegionName(null);
    setQuery(country.name);
    setIsOpen(false);
  };

  return (
    <Wrapper>
      <InputWrapper>
        <Input
          id="country-exchange-search"
          name="country-exchange-search"
          type="text"
          autoComplete="off"
          placeholder="Tapez le nom du pays…"
          value={query}
          disabled={isLoading}
          onChange={(event) => {
            setQuery(event.target.value);
            setSelectedCountry(null);
            setSelectedRegionName(null);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setTimeout(() => setIsOpen(false), 120)}
        />
        {isOpen && matches.length === 0 && query.trim() !== '' && (
          <Dropdown>
            <NoResultItem>Aucun pays ne correspond à votre recherche.</NoResultItem>
          </Dropdown>
        )}
        {isOpen && matches.length > 0 && (
          <Dropdown>
            {matches.map((country) => (
              <DropdownItem
                key={country.iso}
                onMouseDown={(event) => {
                  event.preventDefault();
                  selectCountry(country);
                }}
              >
                <span>{country.flag}</span>
                <span>{country.name}</span>
              </DropdownItem>
            ))}
          </Dropdown>
        )}
      </InputWrapper>

      {selectedCountry?.multiregion && (
        <RegionSelect
          aria-label={`État ou province — ${selectedCountry.name}`}
          value={selectedRegionName || ''}
          onChange={(event) => setSelectedRegionName(event.target.value || null)}
        >
          <option value="">Sélectionnez votre État / province</option>
          {selectedCountry.regions?.map((region) => (
            <option key={region.name} value={region.name}>{region.name}</option>
          ))}
        </RegionSelect>
      )}

      {result && (
        <ResultPanel>
          <ResultHeading>
            <span>{result.flag}</span>
            <span>{result.name}</span>
            <Badge tone={toneForStatus(result.status)}>{labelForStatus(result.status)}</Badge>
          </ResultHeading>
          <ResultRow>
            <ResultRowLabel>Procédure standard : </ResultRowLabel>
            {result.statusText}
          </ResultRow>
          {result.showSpecialRow && (
            <ResultRow>
              <ResultRowLabel>Mission temporaire en France : </ResultRowLabel>
              {result.specialText}
            </ResultRow>
          )}
          {(result.status === 'oui' || result.status === 'conditionnel') && (
            <Btn
              type="button"
              name="country-exchange-cta-ants"
              text="Accéder au service officiel"
              block
              onClick={() => window.open(ANTS_EXCHANGE_URL, '_blank', 'noopener')}
            />
          )}
          {result.status === 'non' && (
            <ResultLink href={NEPH_FLOW_URL}>Obtenir mon numéro NEPH</ResultLink>
          )}
        </ResultPanel>
      )}
    </Wrapper>
  );
};

export { CountryExchangeTool };
